"use client";

import Link from "next/link";
import { formatShortDateTime } from "@/lib/dates";
import { domainOf } from "@/lib/linkDetect";
import { documentPath, formatFileSize } from "@/lib/documents";
import type { LinkPreview as ThetaLinkPreview } from "@theta/api-client";

/**
 * THE LINK CARD — one structure, two sources.
 *
 * The shape is the one people already read without thinking: a wide image on
 * top, then a strip carrying domain, headline and standfirst. That strip is
 * what makes it a *card* rather than a picture with text near it, so it is
 * always present even when there is no image — an image-less card is the strip
 * alone, not a different component.
 *
 * The image is 1.91:1 because that is the ratio every OpenGraph image is
 * authored to. Cropping to a squarer box is what makes other people's link
 * cards look broken.
 *
 * At that ratio the card is mostly picture, so it carries its own width
 * instead of filling whatever holds it — see `LINK_CARD_FRAME`.
 *
 * ON A PHONE IT TURNS ON ITS SIDE (OSO-584 §1). 1.91:1 across a ~390 pt
 * viewport is ~185 pt of picture with the strip under it — a third of the
 * screen spent on one link, which is what put the post itself out of frame in
 * the report. Below `sm` the same two parts sit side by side instead: text
 * left, a 120 px thumbnail right, ~90 pt in total. That is not a new shape to
 * learn — it is exactly the one `LinkPreview` already draws for a migrated
 * onebox, so the two cards a scroll apart still agree, and the divider between
 * the parts goes with it because that layout has never had one.
 *
 * The thumbnail is the one place the ratio does give: as a flex item it takes
 * the strip's height and `object-cover` crops to it. A 120 px thumbnail reads
 * as a thumbnail; it is the full-width hero that has to keep the author's
 * framing.
 *
 * The strip sits on `bg-surface`, the same ground every other card in the UI
 * stands on. It used to be a grey fill inside a translucent grey shell, which
 * against the feed's own grey read as one flat block with a photo in it
 * (OSO-521). The fill therefore belongs to `Shell` — which also keeps the
 * hover to a single element — and `Strip` only draws the rule above itself.
 */

/**
 * Capped and centred, and shared with `LinkPreview`, the other card shape a
 * body can produce. Both are placed by whatever renders that body — a feed
 * post, a comment, a DM bubble — so a cap applied by the caller would have to
 * be applied by every caller, and the one that forgot would be the DM, where
 * the card was wider than the chat dock itself.
 */
export const LINK_CARD_FRAME = "mx-auto w-full max-w-[400px]";

interface ExternalPreviewData {
  title?: string | null;
  description?: string | null;
  image?: string | null;
  siteName?: string | null;
}

function Shell({
  href,
  internal,
  children,
}: {
  href: string;
  internal: boolean;
  children: React.ReactNode;
}) {
  // `flex-row-reverse` puts the image — always the first child — on the RIGHT
  // of the strip, which is the side `LinkPreview` has always kept it on.
  const className =
    `${LINK_CARD_FRAME} flex flex-row-reverse overflow-hidden rounded-xl border border-border ` +
    "sm:block bg-surface transition-colors hover:bg-surface-2 focus-visible:outline-2 " +
    "focus-visible:outline-offset-2 focus-visible:outline-primary";
  // An internal link stays in the app — sending someone to another Theta page
  // through a new tab loses their place for no reason.
  return internal ? (
    <Link href={href} className={className}>{children}</Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

function Image({ src, alt = "" }: { src: string; alt?: string }) {
  return (
    <div className="w-[120px] shrink-0 bg-muted sm:aspect-[1.91/1] sm:w-full">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover"
        onError={(e) => {
          // A dead image must collapse the frame, not leave a grey hole.
          const box = (e.target as HTMLImageElement).parentElement;
          if (box) box.style.display = "none";
        }}
      />
    </div>
  );
}

/** The strip. `eyebrow` is the domain for a link, the kind for a Theta thing. */
function Strip({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub?: string | null;
}) {
  return (
    <div className="min-w-0 flex-1 px-3 py-2.5 sm:border-t sm:border-border">
      <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">
        {eyebrow}
      </div>
      <div className="mt-0.5 line-clamp-2 text-sm font-semibold leading-snug text-foreground">
        {title}
      </div>
      {sub ? (
        <div className="mt-0.5 line-clamp-1 text-xs leading-relaxed text-muted-foreground">
          {sub}
        </div>
      ) : null}
    </div>
  );
}

/** A link to somebody else's page. */
function ExternalLinkCard({
  url,
  data,
}: {
  url: string;
  data: ExternalPreviewData;
}) {
  const domain = domainOf(url);
  return (
    <Shell href={url} internal={false}>
      {data.image ? <Image src={data.image} /> : null}
      <Strip
        eyebrow={data.siteName?.trim() || domain}
        title={data.title?.trim() || domain}
        sub={data.description}
      />
    </Shell>
  );
}

const PRIVACY_LABEL: Record<string, string> = {
  public_group: "Offentlig gruppe",
  private_group: "Privat gruppe",
  secret_group: "Hemmelig gruppe",
};

/**
 * The kinds this build can draw — and the compiler holds it to the branches.
 *
 * A caller that renders CHROME around the card needs to know in advance whether
 * there will be a card, because a `null` card leaves the chrome floating on its
 * own (OSO-616: the composer's dismiss button over an empty frame). It cannot
 * ask by rendering and looking.
 *
 * WRITTEN AS AN EXHAUSTIVE RECORD, NOT A BARE `Set`. A `new Set([...])` of
 * strings is tied to the union by nothing at all: leaving a kind out, or
 * mistyping one, compiles. And the cost is not cosmetic — a kind missing here
 * is one the AUTHOR never sees while composing, so they cannot dismiss it, and
 * `dismissedLinkPreviewUrl` goes out null and publishes the card they were
 * never shown. Keying the record by `ThetaLinkPreview["kind"]` makes that
 * omission the same compile error the missing branch already is.
 */
const DRAWABLE: Record<ThetaLinkPreview["kind"], true> = {
  external: true,
  group: true,
  invite: true,
  document: true,
  event: true,
  post: true,
};

const DRAWABLE_KINDS = new Set<string>(Object.keys(DRAWABLE));

export function isDrawableLinkPreview(preview: ThetaLinkPreview): boolean {
  return DRAWABLE_KINDS.has(preview.kind);
}

/**
 * A link to something inside Theta, rendered as a share rather than a link.
 *
 * Everything shown here was resolved by the server under the READER'S OWN
 * visibility — see `GET /api/v1/link-previews`. When they may not see the
 * target the server answers 404 and the caller falls back to a plain link, so
 * this component never has to decide what to hide. It should not start.
 */
export function ThetaShareCard({ preview }: { preview: ThetaLinkPreview }) {
  if (preview.kind === "external") {
    return (
      <ExternalLinkCard
        url={preview.url}
        data={{
          title: preview.title,
          description: preview.description,
          image: preview.imageUrl,
          siteName: preview.siteName,
        }}
      />
    );
  }

  if (preview.kind === "group") {
    return (
      <Shell href={`/grupper/${preview.slug}`} internal>
        {preview.imageUrl ? <Image src={preview.imageUrl} /> : null}
        <Strip
          eyebrow={
            [
              PRIVACY_LABEL[preview.privacyLevel] ?? "Gruppe",
              preview.memberCount != null
                ? `${preview.memberCount} medlemmer`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")
          }
          title={preview.name}
          sub={preview.description}
        />
      </Shell>
    );
  }

  /**
   * AN OPEN INVITATION, WHICH IS NOT THE GROUP IT LEADS TO.
   *
   * It used to fall through to the external crawler and render the site-wide
   * OpenGraph tags — "Theta / Find dit fællesskab" — so an invitation pasted
   * into a conversation said nothing about who was being invited where
   * (OSO-456). The group NAME is all the server sends, and all it may: the
   * token is public and the group may be secret, so the card cannot say more
   * than the token already does. No cover, no member count, no privacy label.
   *
   * `preview.path` AND NOT `preview.url`, for the same reason every other
   * internal kind builds its own href. The server resolves a link preview by
   * PATHNAME and ignores the host, so `https://phish.example/invitationer/
   * link/<a real token>` resolves — and a headline like this one, on a card
   * that shows no domain at all, is a very good disguise for somebody else's
   * site. The path is built by the handler from the token it just verified.
   */
  if (preview.kind === "invite") {
    return (
      <Shell href={preview.path} internal>
        <Strip eyebrow="Invitation" title={`Du er inviteret til ${preview.groupName}`} />
      </Shell>
    );
  }

  /**
   * A FILE IN A GROUP'S DOCUMENT TAB — OSO-613.
   *
   * NO IMAGE, and not for want of one: the card links to the document's page,
   * never to its bytes, so there is nothing to show a thumbnail of that would
   * not first have to be a signed media URL sitting in a preview — which is
   * exactly what OSO-612's permanent address exists to avoid. The strip alone
   * is already a card; see the note at the top of this file.
   *
   * The badge line carries what somebody deciding whether to open it needs:
   * the file type, how big it is, and which folder it is filed in. The GROUP
   * is in the eyebrow beside "Dokument", because a link that arrived in a
   * message is a link from somewhere.
   *
   * Everything here was resolved from the `documents` row UNDER THIS READER'S
   * OWN VISIBILITY. A reader without access never reaches this branch — the
   * server answered 404 and `BodyLinkPreview` rendered the plain anchor — so a
   * private group's filenames do not travel with a forwarded link.
   */
  if (preview.kind === "document") {
    const facts = [
      preview.extension?.toUpperCase() || null,
      formatFileSize(preview.filesize) || null,
      preview.folder,
    ].filter(Boolean);
    return (
      <Shell
        href={documentPath({ id: preview.id, group: { slug: preview.groupSlug } })}
        internal
      >
        <Strip
          eyebrow={["Dokument", preview.groupName].filter(Boolean).join(" · ")}
          title={preview.title}
          sub={facts.length > 0 ? facts.join(" · ") : null}
        />
      </Shell>
    );
  }

  if (preview.kind === "event") {
    return (
      <Shell href={`/begivenheder/${preview.id}`} internal>
        {preview.imageUrl ? <Image src={preview.imageUrl} /> : null}
        <Strip
          eyebrow={["Begivenhed", formatShortDateTime(preview.startsAt)]
            .filter(Boolean)
            .join(" · ")}
          title={preview.title}
          sub={preview.location}
        />
      </Shell>
    );
  }

  /**
   * `post` ASKS FOR ITS OWN KIND, AND ANYTHING UNRECOGNISED DRAWS NOTHING.
   *
   * This branch used to be the fall-through: every `kind` the switch above did
   * not claim landed here and was read as a post. That made adding a member to
   * the `LinkPreview` union — which the union exists to allow — break every
   * browser still running the previous build, because an already-loaded page
   * keeps its old JavaScript and meets the new `kind` at runtime.
   *
   * It is not hypothetical. OSO-613 added `kind: "document"`, and thirteen
   * minutes after the deploy a client on the previous release fell in here,
   * found `excerpt` undefined and threw `Cannot read properties of undefined
   * (reading 'trim')` on a group page (Sentry THETA-APP-C, OSO-616). The
   * contract gate had flagged exactly this and it was merged as an accepted
   * break (O-31) on the reasoning that the only consumer ships with the
   * server — true of the bundle, false of the tab already open.
   *
   * Returning `null` degrades to what the body already renders underneath: a
   * plain link. A reader on an old build misses a card; nobody loses the page.
   */
  if (preview.kind !== "post") {
    /**
     * AND THIS BUILD STILL HAS TO FAIL LOUDLY WHEN THE MISSING BRANCH IS OURS.
     *
     * The `return null` above is for the OLD bundle meeting a NEW kind. It must
     * not also excuse the new bundle: before this guard existed, adding a member
     * to the union narrowed `preview` at `excerpt.trim()` and broke the build,
     * which is exactly what forced OSO-613 to write its `document` branch. A
     * bare `return null` would have handed that away — the next kind would
     * typecheck, pass every test, and render no card at all on the build that
     * was supposed to draw it.
     *
     * `never` keeps both: a runtime no-op for the tab that is behind, a compile
     * error the moment THIS file is the one missing a branch.
     */
    const unhandled: never = preview;
    void unhandled;
    return null;
  }

  const href = preview.groupSlug
    ? `/grupper/${preview.groupSlug}/opslag/${preview.id}`
    : `/opslag/${preview.id}`;
  return (
    <Shell href={href} internal>
      {preview.imageUrl ? <Image src={preview.imageUrl} /> : null}
      <Strip
        eyebrow={["Opslag", preview.groupName].filter(Boolean).join(" · ")}
        // An image-only post has no text; naming the author is more use than
        // an empty headline.
        title={preview.excerpt.trim() || `Opslag af ${preview.authorName}`}
        sub={preview.excerpt.trim() ? preview.authorName : null}
      />
    </Shell>
  );
}

export function LinkCardSkeleton() {
  return (
    <div
      className={`${LINK_CARD_FRAME} flex flex-row-reverse overflow-hidden rounded-xl border border-border bg-surface sm:block`}
    >
      <div className="w-[120px] shrink-0 animate-pulse bg-muted sm:aspect-[1.91/1] sm:w-full" />
      <div className="min-w-0 flex-1 space-y-2 px-3 py-2.5 sm:border-t sm:border-border">
        <div className="h-2.5 w-20 animate-pulse rounded bg-muted" />
        <div className="h-3.5 w-3/4 animate-pulse rounded bg-muted" />
        <div className="h-3 w-full animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}
